import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { SpecialNeedsAndDietPicker } from '../components/SpecialNeedsAndDietPicker';
import { EMPTY_SPECIAL_NEEDS } from '../utils/childFields';

function EnrollmentWizard({ user, onComplete, onCancel }) {
  const [currentStep, setCurrentStep] = useState(1);
  // Lazy-loaded Terms & Conditions for the parent's organisation. Fetched
  // on mount via /api/auth/me + /api/organizations/me — falls back to the
  // public /api/organizations/by-slug/{slug}/branding endpoint if needed.
  const [orgTerms, setOrgTerms] = useState(null); // { text, url, version, updated_at }
  const [enrollmentData, setEnrollmentData] = useState({
    child_first_name: '',
    child_last_name: '',
    child_date_of_birth: '',
    child_age_group: 'toddler',
    child_photo_url: '',
    // Stable S3 file id — kept so the server can refresh presigned URLs.
    child_photo_file_id: '',
    // Top-level structured SEND + dietary metadata (mirrors Child model so
    // enrollment → child propagation is a straight copy).
    child_dietary_requirements: [],
    child_special_needs: { ...EMPTY_SPECIAL_NEEDS },
    // Which services the family is signing this child up for.
    // Drives whether school details are mandatory (breakfast_club &
    // after_school_club need a registered school + collection location;
    // holiday_club / nursery_full_day / other can be optional).
    services_required: [],
    school_info: {
      attends_school: false,
      school_name: '',
      school_address: '',
      school_postcode: '',
      school_class: '',
      teacher_name: '',
      school_start_time: '08:45',
      school_end_time: '15:15',
      school_meeting_point: '',
      school_contact_number: '',
      school_notes: ''
    },
    parent_info: {
      // Captured on Step 2 so the signed application has the parent's
      // identity even if the underlying user account changes later.
      full_name: '',
      email: '',
      address_line1: '',
      address_line2: '',
      city: '',
      postcode: '',
      phone: '',
      emergency_contact: {
        name: '',
        relationship: '',
        phone: '',
        email: '',
        can_pickup: false
      },
      relationship_to_child: 'parent',
      occupation: ''
    },
    medical_info: {
      allergies: [],
      dietary_restrictions: [],
      medications: [],
      medical_conditions: [],
      doctor_name: '',
      doctor_phone: '',
      insurance_provider: '',
      insurance_policy_number: '',
      special_needs: '',
      notes: ''
    },
    authorized_pickups: [],
    preferred_start_date: '',
    preferred_days: [],
    preferred_sessions: [],
    // Payment preferences captured at application time. See
    // enrollment_models.py:EnrollmentApplication for the doc on values.
    payment_plan: 'pay_as_you_go',
    billing_terms: 'standard',
    ofsted_consents: {
      emergency_medical_treatment: { granted: false, signed_at: null, notes: '' },
      medication_administration: { granted: false, signed_at: null, notes: '' },
      sun_cream_application: { granted: false, signed_at: null, notes: '' },
      outings_excursions: { granted: false, signed_at: null, notes: '' },
      photography_internal_learning: { granted: false, signed_at: null, notes: '' },
      photography_website: { granted: false, signed_at: null, notes: '' },
      photography_social_media: { granted: false, signed_at: null, notes: '' },
      photography_marketing: { granted: false, signed_at: null, notes: '' },
      information_sharing_health: { granted: false, signed_at: null, notes: '' },
      information_sharing_school: { granted: false, signed_at: null, notes: '' },
      information_sharing_authority: { granted: false, signed_at: null, notes: '' },
      information_sharing_safeguarding: { granted: false, signed_at: null, notes: '' },
      data_protection_gdpr: { granted: false, signed_at: null, notes: '' },
      parent_handbook: { granted: false, signed_at: null, notes: '' },
      behavior_policy: { granted: false, signed_at: null, notes: '' },
      complaints_procedure: { granted: false, signed_at: null, notes: '' }
    },
    parent_signature: {
      signature_data: '',
      signature_type: 'typed',
      signed_at: null,
      full_name: ''
    },
    // Legacy fields
    terms_accepted: false,
    handbook_acknowledged: false,
    photo_consent: false
  });

  const [newPickup, setNewPickup] = useState({
    name: '',
    relationship: '',
    phone: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signatureType, setSignatureType] = useState('typed'); // 'typed' or 'drawn'

  // Pre-fill parent identity from the signed-in user on first mount so
  // the parent doesn't have to retype their own name/email/phone. The
  // wizard still lets them edit the values for accuracy. If the caller
  // didn't pass `user` we silently skip — the parent can still type
  // their details manually.
  useEffect(() => {
    if (!user) return;
    setEnrollmentData((prev) => ({
      ...prev,
      parent_info: {
        ...prev.parent_info,
        full_name: prev.parent_info?.full_name || user.full_name || '',
        email: prev.parent_info?.email || user.email || '',
        phone: prev.parent_info?.phone || user.phone || '',
      },
    }));
    // We deliberately run only on mount — once the parent edits the
    // field we never overwrite their input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist wizard progress to localStorage so a parent can close the
  // browser and resume later. Keyed per-user so two parents on the same
  // device never see each other's data.
  const [draftStorageKey] = useState(() => 'wizkids.enrollment_draft.v1');
  // Tracks whether the wizard has already offered "Resume?" once for
  // this mount so we don't keep asking the user.
  const [resumeOffered, setResumeOffered] = useState(false);
  // Post-submit confirmation screen state. Holds the freshly-submitted
  // enrollment so we can surface its printable URL + "Enroll another
  // child" CTA without leaving the wizard.
  const [submittedEnrollment, setSubmittedEnrollment] = useState(null);

  // Restore an in-progress draft on mount (if any). Wrapped in
  // try/catch so a malformed payload never breaks the wizard.
  useEffect(() => {
    if (resumeOffered) return;
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) { setResumeOffered(true); return; }
      const saved = JSON.parse(raw);
      if (!saved || !saved.data) { setResumeOffered(true); return; }
      const meaningful = saved.data.child_first_name || saved.data.child_last_name
        || saved.data.parent_info?.address_line1 || saved.data.parent_info?.phone;
      if (!meaningful) { setResumeOffered(true); return; }
      const ok = window.confirm(
        `You have an unfinished enrollment from ${new Date(saved.savedAt).toLocaleString()}.\n\nResume where you left off?`
      );
      if (ok) {
        setEnrollmentData((prev) => ({ ...prev, ...saved.data }));
        if (Number.isInteger(saved.step) && saved.step >= 1 && saved.step <= 6) {
          setCurrentStep(saved.step);
        }
      } else {
        localStorage.removeItem(draftStorageKey);
      }
    } catch (_) { /* ignore corrupted draft */ }
    setResumeOffered(true);
  }, [draftStorageKey, resumeOffered]);

  // Auto-save the wizard state to localStorage on every change. We
  // throttle by relying on React's render coalescing — it's cheap.
  useEffect(() => {
    if (!resumeOffered) return; // wait until restore prompt resolved
    if (submittedEnrollment) return; // never overwrite once submitted
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify({
        savedAt: new Date().toISOString(),
        step: currentStep,
        data: enrollmentData,
      }));
    } catch (_) { /* storage quota — non-fatal */ }
  }, [enrollmentData, currentStep, draftStorageKey, resumeOffered, submittedEnrollment]);

  // Load the org's Terms & Conditions on mount so the wizard can render
  // the inline scrollable card. We pull from the authenticated
  // /api/organizations/me endpoint (covers the most cases), falling back
  // to the public branding endpoint if needed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const org = await api.getMyOrganization();
        if (cancelled) return;
        const s = (org && org.settings) || {};
        if (s.terms_and_conditions) {
          setOrgTerms({
            text: s.terms_and_conditions,
            url: s.terms_url || null,
            version: Number.isFinite(s.terms_version) ? s.terms_version : 1,
            updated_at: s.terms_updated_at || null,
          });
          return;
        }
        // Fallback: public branding by slug.
        if (org && org.slug) {
          const b = await api.getOrgBrandingBySlug(org.slug);
          if (!cancelled && b && b.terms_and_conditions) {
            setOrgTerms({
              text: b.terms_and_conditions,
              url: b.terms_url || null,
              version: Number.isFinite(b.terms_version) ? b.terms_version : 1,
              updated_at: b.terms_updated_at || null,
            });
          }
        }
      } catch (err) {
        // T&Cs are optional — wizard still works without them.
        // eslint-disable-next-line no-console
        console.warn('Could not load Terms & Conditions:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Child photo upload state (S3-backed via /api/files/upload).
  const [childPhotoUploading, setChildPhotoUploading] = useState(false);
  const [childPhotoError, setChildPhotoError] = useState(null);

  const handleChildPhotoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setChildPhotoError(null);
    if (!file.type.startsWith('image/')) {
      setChildPhotoError('Please pick an image (PNG, JPEG or WebP).');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setChildPhotoError('Image is too large. Max 5 MB.');
      e.target.value = '';
      return;
    }
    try {
      setChildPhotoUploading(true);
      const res = await api.uploadFile(file, { category: 'generic', linkedType: 'child' });
      setEnrollmentData(prev => ({
        ...prev,
        child_photo_url: res.url,
        child_photo_file_id: res.file_id,
      }));
    } catch (err) {
      setChildPhotoError(err.message || 'Upload failed');
    } finally {
      setChildPhotoUploading(false);
      // Clear the input so picking the same file again still fires onChange.
      e.target.value = '';
    }
  };

  const handleChildPhotoClear = () => {
    setEnrollmentData(prev => ({ ...prev, child_photo_url: '', child_photo_file_id: '' }));
    setChildPhotoError(null);
  };

  const totalSteps = 6; // Updated from 5 to 6

  const handleChange = (field, value) => {
    setEnrollmentData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If age_group changes to primary, enable school info
    if (field === 'child_age_group' && value === 'primary') {
      setEnrollmentData(prev => ({
        ...prev,
        school_info: {
          ...prev.school_info,
          attends_school: true
        }
      }));
    }
  };

  const handleSchoolInfoChange = (field, value) => {
    setEnrollmentData(prev => ({
      ...prev,
      school_info: {
        ...prev.school_info,
        [field]: value
      }
    }));
  };

  // Service-type chooser (Step 1). Drives whether school details on
  // step 1 are *required* — Breakfast Club + After-School Club need
  // a registered school + collection location, the rest don't.
  const SERVICE_TYPES = [
    { code: 'breakfast_club', label: 'Breakfast Club', requiresSchool: true,
      hint: 'Pick-up from your child\'s school in the morning.' },
    { code: 'after_school_club', label: 'After-School Club', requiresSchool: true,
      hint: 'Collection from school at end of day.' },
    { code: 'holiday_club', label: 'Holiday Club', requiresSchool: false,
      hint: 'Full-day care during school holidays.' },
    { code: 'nursery_full_day', label: 'Full-day Nursery', requiresSchool: false,
      hint: 'Pre-school or full-day childcare at our centre.' },
    { code: 'other', label: 'Other', requiresSchool: false,
      hint: 'Bespoke arrangement — please describe in notes.' },
  ];
  const SCHOOL_REQUIRED_SERVICES = ['breakfast_club', 'after_school_club'];

  const toggleServiceRequired = (code) => {
    setEnrollmentData(prev => {
      const current = prev.services_required || [];
      const next = current.includes(code)
        ? current.filter(c => c !== code)
        : [...current, code];
      // Guard: if the family ends up on Holiday-Club-only, forcibly
      // reset any previously-picked monthly plan back to pay-as-you-go
      // so we never submit an invalid combo (backend rejects it too).
      let newPaymentPlan = prev.payment_plan;
      if (next.length === 1 && next[0] === 'holiday_club' && (prev.payment_plan || '').startsWith('monthly_')) {
        newPaymentPlan = 'pay_as_you_go';
      }
      return { ...prev, services_required: next, payment_plan: newPaymentPlan };
    });
  };

  // Convenience: does the family's chosen mix REQUIRE school info?
  const schoolInfoIsRequired = (data = enrollmentData) => {
    const services = data.services_required || [];
    return services.some((s) => SCHOOL_REQUIRED_SERVICES.includes(s));
  };
  // Should we even SHOW the school details fieldset? Yes whenever any
  // service is selected — for non-school services it's just optional
  // helpful context (e.g. occasional school pickups for holiday clubs).
  const schoolInfoIsShown = (data = enrollmentData) => {
    const services = data.services_required || [];
    return services.length > 0 || data.child_age_group === 'primary';
  };

  const handleNestedChange = (parent, field, value) => {
    setEnrollmentData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setEnrollmentData(prev => ({
      ...prev,
      parent_info: {
        ...prev.parent_info,
        emergency_contact: {
          ...prev.parent_info.emergency_contact,
          [field]: value
        }
      }
    }));
  };

  const handleArrayChange = (parent, field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setEnrollmentData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: items
      }
    }));
  };

  const handleDayToggle = (day) => {
    setEnrollmentData(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter(d => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  const handleSessionToggle = (session) => {
    setEnrollmentData(prev => ({
      ...prev,
      preferred_sessions: prev.preferred_sessions.includes(session)
        ? prev.preferred_sessions.filter(s => s !== session)
        : [...prev.preferred_sessions, session]
    }));
  };

  const handleConsentToggle = (consentKey) => {
    setEnrollmentData(prev => ({
      ...prev,
      ofsted_consents: {
        ...prev.ofsted_consents,
        [consentKey]: {
          ...prev.ofsted_consents[consentKey],
          granted: !prev.ofsted_consents[consentKey].granted,
          signed_at: new Date().toISOString()
        }
      }
    }));
  };

  const handleSignatureChange = (value) => {
    setEnrollmentData(prev => ({
      ...prev,
      parent_signature: {
        ...prev.parent_signature,
        signature_data: signatureType === 'typed' ? `typed:${value}` : value,
        signature_type: signatureType,
        full_name: signatureType === 'typed' ? value : prev.parent_signature.full_name,
        signed_at: new Date().toISOString()
      }
    }));
  };

  const addAuthorizedPickup = () => {
    if (newPickup.name && newPickup.relationship && newPickup.phone) {
      setEnrollmentData(prev => ({
        ...prev,
        authorized_pickups: [...prev.authorized_pickups, { ...newPickup, photo_id_verified: false }]
      }));
      setNewPickup({ name: '', relationship: '', phone: '' });
    }
  };

  const removeAuthorizedPickup = (index) => {
    setEnrollmentData(prev => ({
      ...prev,
      authorized_pickups: prev.authorized_pickups.filter((_, i) => i !== index)
    }));
  };

  // Per-step required-field schema. Each entry produces a granular
  // {step, fieldKey, fieldLabel, message} error so we can highlight the
  // exact input on the right step rather than just showing a vague
  // "fix something" toast. The fieldKey matches the data-testid of the
  // input on its step so we can scroll to it.
  const STEP_NAMES = {
    1: 'Child information',
    2: 'Parent details',
    3: 'Medical & health',
    4: 'Schedule preferences',
    5: 'Terms & policies',
    6: 'Consents & signature',
  };

  // Validate every required field. Returns an array of issue objects.
  // Empty array = valid.
  const validateAllIssues = () => {
    const data = enrollmentData;
    const issues = [];

    // Step 1 — Child basics
    if (!data.child_first_name?.trim()) issues.push({ step: 1, fieldKey: 'child_first_name', fieldLabel: "Child's first name", message: 'Please enter the child\'s first name.' });
    if (!data.child_last_name?.trim()) issues.push({ step: 1, fieldKey: 'child_last_name', fieldLabel: "Child's last name", message: 'Please enter the child\'s last name.' });
    if (!data.child_date_of_birth) issues.push({ step: 1, fieldKey: 'child_date_of_birth', fieldLabel: 'Date of birth', message: 'Please enter the child\'s date of birth.' });

    // Step 1 — School info: required for school-attending children
    // (Breakfast Club and After-School Club). Optional for Holiday
    // Club, Full-day Nursery and Other.
    const services = data.services_required || [];
    const SCHOOL_REQUIRED_SERVICES = ['breakfast_club', 'after_school_club'];
    const schoolRequired = services.some((s) => SCHOOL_REQUIRED_SERVICES.includes(s));
    if (services.length === 0) {
      issues.push({ step: 1, fieldKey: 'services_required', fieldLabel: 'Services required', message: 'Please tell us which services you\'re signing up for.' });
    }
    if (schoolRequired) {
      const s = data.school_info || {};
      if (!s.school_name?.trim()) issues.push({ step: 1, fieldKey: 'school_name', fieldLabel: 'School name', message: 'School name is required for Breakfast / After-School Club.' });
      if (!s.school_address?.trim()) issues.push({ step: 1, fieldKey: 'school_address', fieldLabel: 'School address', message: 'School address is required.' });
      if (!s.school_postcode?.trim()) issues.push({ step: 1, fieldKey: 'school_postcode', fieldLabel: 'School postcode', message: 'School postcode is required.' });
      if (!s.school_class?.trim()) issues.push({ step: 1, fieldKey: 'school_class', fieldLabel: 'Class / Year', message: 'Class or year group is required.' });
      if (!s.teacher_name?.trim()) issues.push({ step: 1, fieldKey: 'teacher_name', fieldLabel: 'Class teacher', message: 'Teacher name is required.' });
      if (!s.school_meeting_point?.trim()) issues.push({ step: 1, fieldKey: 'school_meeting_point', fieldLabel: 'Collection location', message: 'Collection location at school is required so our driver knows where to pick the child up.' });
    }

    // Step 2 — Parent details
    const p = data.parent_info || {};
    if (!p.full_name?.trim()) issues.push({ step: 2, fieldKey: 'parent_full_name', fieldLabel: 'Parent / Guardian full name', message: 'Please enter your full name.' });
    if (!p.email?.trim()) issues.push({ step: 2, fieldKey: 'parent_email', fieldLabel: 'Parent / Guardian email', message: 'Please enter a contact email.' });
    if (!p.address_line1?.trim()) issues.push({ step: 2, fieldKey: 'address_line1', fieldLabel: 'Address line 1', message: 'Please enter your address.' });
    if (!p.city?.trim()) issues.push({ step: 2, fieldKey: 'city', fieldLabel: 'City', message: 'Please enter your city.' });
    if (!p.postcode?.trim()) issues.push({ step: 2, fieldKey: 'postcode', fieldLabel: 'Postcode', message: 'Please enter your postcode.' });
    if (!p.phone?.trim()) issues.push({ step: 2, fieldKey: 'phone', fieldLabel: 'Phone', message: 'Please enter a contact phone number.' });

    // Step 2 — Emergency contact
    const ec = p.emergency_contact || {};
    if (!ec.name?.trim()) issues.push({ step: 2, fieldKey: 'emergency_name', fieldLabel: 'Emergency contact name', message: 'Emergency contact name is required.' });
    if (!ec.relationship?.trim()) issues.push({ step: 2, fieldKey: 'emergency_relationship', fieldLabel: 'Emergency contact relationship', message: 'Emergency contact relationship is required.' });
    if (!ec.phone?.trim()) issues.push({ step: 2, fieldKey: 'emergency_phone', fieldLabel: 'Emergency contact phone', message: 'Emergency contact phone is required.' });

    // Step 5 — Terms
    if (!data.terms_accepted) issues.push({ step: 5, fieldKey: 'terms_accepted', fieldLabel: 'Terms & Conditions', message: 'You must accept the Terms & Conditions to proceed.' });
    if (!data.handbook_acknowledged) issues.push({ step: 5, fieldKey: 'handbook_acknowledged', fieldLabel: 'Parent handbook', message: 'You must acknowledge the parent handbook.' });

    // Step 6 — Required consents + signature
    const consents = data.ofsted_consents || {};
    const requiredConsents = [
      ['emergency_medical_treatment', 'Emergency medical treatment'],
      ['data_protection_gdpr', 'Data protection (GDPR)'],
      ['parent_handbook', 'Parent handbook acknowledgement'],
    ];
    for (const [key, label] of requiredConsents) {
      if (!consents[key]?.granted) {
        issues.push({ step: 6, fieldKey: `consent_${key}`, fieldLabel: label, message: `Required consent not granted: ${label}.` });
      }
    }
    const sig = data.parent_signature || {};
    if (!sig.signature_data) issues.push({ step: 6, fieldKey: 'signature_data', fieldLabel: 'Parent signature', message: 'Please provide your signature.' });
    if (!sig.full_name?.trim()) issues.push({ step: 6, fieldKey: 'signature_full_name', fieldLabel: 'Full name', message: 'Please type your full name to sign.' });

    return issues;
  };

  // Track the current set of issues so each step can highlight its own
  // failing fields. We keep this on state (rather than recomputing on
  // every render) so the user sees the highlight ONLY after they've
  // hit Next / Submit and we've actually run the validator.
  const [fieldIssues, setFieldIssues] = useState([]);
  const issuesByStep = (step) => fieldIssues.filter((i) => i.step === step);
  const issueFor = (key) => fieldIssues.find((i) => i.fieldKey === key);
  const errorClass = (key) => (issueFor(key) ? 'has-field-error' : '');
  const fieldErrorEl = (key) => {
    const i = issueFor(key);
    if (!i) return null;
    return (
      <div
        style={{ color: 'hsl(0 70% 38%)', fontSize: '0.78rem', marginTop: 2 }}
        data-testid={`field-error-${key}`}
      >
        {i.message}
      </div>
    );
  };

  // Backwards-compatible thin wrapper used by Next button. Only flags
  // the FIRST issue on the current step (matches old "one banner" UX).
  const validateStep = (step = currentStep) => {
    const data = enrollmentData;
    switch (step) {
      case 1:
        if (!data.child_first_name || !data.child_last_name || !data.child_date_of_birth) {
          return { step: 1, message: 'Please fill in all required child information (name and date of birth).' };
        }
        if (data.child_age_group === 'primary') {
          const s = data.school_info || {};
          if (!s.school_name || !s.school_address || !s.school_postcode || !s.school_class || !s.teacher_name || !s.school_meeting_point) {
            return { step: 1, message: 'School information is required for primary-age children.' };
          }
        }
        return null;
      case 2: {
        const p = data.parent_info || {};
        if (!p.address_line1 || !p.city || !p.postcode || !p.phone) {
          return { step: 2, message: 'Please fill in all required parent details (address, city, postcode, phone).' };
        }
        const ec = p.emergency_contact || {};
        if (!ec.name || !ec.phone || !ec.relationship) {
          return { step: 2, message: 'Emergency contact name, phone and relationship are required.' };
        }
        return null;
      }
      case 5:
        if (!data.terms_accepted) {
          return { step: 5, message: 'You must accept the terms and conditions to proceed.' };
        }
        if (!data.handbook_acknowledged) {
          return { step: 5, message: 'You must acknowledge the parent handbook.' };
        }
        return null;
      case 6: {
        const consents = data.ofsted_consents || {};
        const required = ['emergency_medical_treatment', 'data_protection_gdpr', 'parent_handbook'];
        for (const key of required) {
          if (!consents[key]?.granted) {
            const human = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            return { step: 6, message: `Required consent not granted: ${human}.` };
          }
        }
        const sig = data.parent_signature || {};
        if (!sig.signature_data || !sig.full_name) {
          return { step: 6, message: 'Please provide your signature and full name.' };
        }
        return null;
      }
      default:
        return null;
    }
  };

  // Returns the first issue across all steps (used to jump on submit).
  const validateAll = () => {
    const issues = validateAllIssues();
    return issues.length ? issues[0] : null;
  };

  const handleNext = () => {
    const issues = validateAllIssues().filter((i) => i.step === currentStep);
    if (issues.length) {
      setFieldIssues(issues);
      setError(`Please complete the following on this step: ${issues.map((i) => i.fieldLabel).join(', ')}.`);
      try {
        const modal = document.querySelector('[data-testid="enrollment-wizard"]');
        if (modal) modal.scrollTop = 0;
      } catch (_) { /* noop */ }
      return;
    }
    setFieldIssues([]);
    setError('');
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  // Convert the wizard state into a backend-safe payload. This is the
  // single most important fix for "Submit Enrollment does nothing" —
  // Pydantic v2 rejects empty-string optional dates and `null` datetime
  // fields, so we coerce them to either omitted or a valid ISO timestamp.
  const buildSubmitPayload = () => {
    const nowIso = new Date().toISOString();
    const safeConsents = {};
    for (const [key, value] of Object.entries(enrollmentData.ofsted_consents || {})) {
      // Every consent record MUST have a datetime `signed_at`. When the
      // parent hasn't toggled the switch yet we stamp "now" so the
      // record is still well-formed (granted=false is fine).
      safeConsents[key] = {
        granted: !!value?.granted,
        signed_at: value?.signed_at || nowIso,
        notes: value?.notes || '',
      };
    }
    const sig = enrollmentData.parent_signature || {};
    const safeSignature = (sig.signature_data || sig.full_name) ? {
      signature_data: sig.signature_data || '',
      signature_type: sig.signature_type || 'typed',
      signed_at: sig.signed_at || nowIso,
      full_name: sig.full_name || '',
    } : null;

    const payload = {
      ...enrollmentData,
      child_special_needs: enrollmentData.child_special_needs?.has_sen
        ? enrollmentData.child_special_needs
        : null,
      ofsted_consents: safeConsents,
      parent_signature: safeSignature,
    };
    // Strip empty optional date — Pydantic rejects '' for Optional[datetime].
    if (!payload.preferred_start_date) delete payload.preferred_start_date;
    // school_info & services_required ARE persisted on the enrollment
    // (and propagated to the Child on approval) — the backend
    // EnrollmentCreate model now defines them so we leave them in.
    return payload;
  };

  // Map a backend Pydantic loc path (e.g. ['body','parent_info','phone'])
  // to the same {step, fieldKey, fieldLabel} shape used by client-side
  // validation, so server-side rejections look identical to the parent.
  const mapServerLocToIssue = (loc, msg) => {
    if (!Array.isArray(loc) || loc.length < 2) return null;
    // Strip the leading 'body' that FastAPI prepends.
    const path = loc[0] === 'body' ? loc.slice(1) : loc;
    const join = path.join('.');
    const M = {
      'child_first_name': { step: 1, fieldKey: 'child_first_name', fieldLabel: "Child's first name" },
      'child_last_name': { step: 1, fieldKey: 'child_last_name', fieldLabel: "Child's last name" },
      'child_date_of_birth': { step: 1, fieldKey: 'child_date_of_birth', fieldLabel: 'Date of birth' },
      'child_age_group': { step: 1, fieldKey: 'child_age_group', fieldLabel: 'Age group' },
      'parent_info.full_name': { step: 2, fieldKey: 'parent_full_name', fieldLabel: 'Parent / Guardian full name' },
      'parent_info.email': { step: 2, fieldKey: 'parent_email', fieldLabel: 'Parent / Guardian email' },
      'parent_info.address_line1': { step: 2, fieldKey: 'address_line1', fieldLabel: 'Address line 1' },
      'parent_info.city': { step: 2, fieldKey: 'city', fieldLabel: 'City' },
      'parent_info.postcode': { step: 2, fieldKey: 'postcode', fieldLabel: 'Postcode' },
      'parent_info.phone': { step: 2, fieldKey: 'phone', fieldLabel: 'Phone' },
      'parent_info.emergency_contact.name': { step: 2, fieldKey: 'emergency_name', fieldLabel: 'Emergency contact name' },
      'parent_info.emergency_contact.phone': { step: 2, fieldKey: 'emergency_phone', fieldLabel: 'Emergency contact phone' },
      'parent_info.emergency_contact.relationship': { step: 2, fieldKey: 'emergency_relationship', fieldLabel: 'Emergency contact relationship' },
      'preferred_start_date': { step: 4, fieldKey: 'preferred_start_date', fieldLabel: 'Preferred start date' },
      'terms_accepted': { step: 5, fieldKey: 'terms_accepted', fieldLabel: 'Terms & Conditions' },
      'handbook_acknowledged': { step: 5, fieldKey: 'handbook_acknowledged', fieldLabel: 'Parent handbook' },
      'parent_signature.signature_data': { step: 6, fieldKey: 'signature_data', fieldLabel: 'Parent signature' },
      'parent_signature.full_name': { step: 6, fieldKey: 'signature_full_name', fieldLabel: 'Full name' },
    };
    if (M[join]) return { ...M[join], message: msg || 'This field is required.' };
    if (join.startsWith('ofsted_consents.')) {
      const key = path[1];
      const human = (key || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      return { step: 6, fieldKey: `consent_${key}`, fieldLabel: `Consent: ${human}`, message: msg || 'Required consent missing.' };
    }
    // Fallback — surface the path so the parent at least sees something.
    return { step: 1, fieldKey: join, fieldLabel: join.replace(/_/g, ' '), message: msg };
  };

  const handleSubmit = async () => {
    // Client-side: collect EVERY missing required field, not just one.
    const issues = validateAllIssues();
    if (issues.length) {
      setFieldIssues(issues);
      const first = issues[0];
      setCurrentStep(first.step);
      setError(`We can't submit yet — ${issues.length} item${issues.length > 1 ? 's' : ''} need${issues.length > 1 ? '' : 's'} attention. Starting with: "${first.fieldLabel}" on Step ${first.step} (${STEP_NAMES[first.step]}).`);
      try {
        const modal = document.querySelector('[data-testid="enrollment-wizard"]');
        if (modal) modal.scrollTop = 0;
      } catch (_) { /* noop */ }
      return;
    }

    setLoading(true);
    setError('');
    setFieldIssues([]);

    try {
      const payload = buildSubmitPayload();
      const enrollment = await api.createEnrollment(payload);
      const submitted = await api.submitEnrollment(enrollment._id || enrollment.id);
      try { localStorage.removeItem(draftStorageKey); } catch (_) { /* noop */ }
      setSubmittedEnrollment(submitted || enrollment);
    } catch (err) {
      // If the server returned structured Pydantic errors, map every one
      // back to a step + field so the parent sees per-field highlights
      // identical to client-side validation.
      if (Array.isArray(err.fieldErrors) && err.fieldErrors.length) {
        const mapped = err.fieldErrors
          .map((d) => mapServerLocToIssue(d.loc, d.msg))
          .filter(Boolean);
        if (mapped.length) {
          setFieldIssues(mapped);
          const first = mapped[0];
          setCurrentStep(first.step);
          setError(`Your provider's system rejected ${mapped.length} field${mapped.length > 1 ? 's' : ''}. Starting with: "${first.fieldLabel}" on Step ${first.step} (${STEP_NAMES[first.step]}).`);
        } else {
          setError(err.message || 'Failed to submit enrollment.');
        }
      } else {
        setError(err.message || 'Failed to submit enrollment.');
      }
    } finally {
      setLoading(false);
    }
  };

  // "Save & continue later" — flushes the current state to localStorage
  // (already auto-saved on every change but we do it explicitly here so
  // the parent gets confirmation) and closes the wizard.
  const handleSaveAndExit = () => {
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify({
        savedAt: new Date().toISOString(),
        step: currentStep,
        data: enrollmentData,
      }));
    } catch (_) { /* noop */ }
    if (onCancel) onCancel();
  };

  // "Enroll another child" CTA on the post-submit screen — resets the
  // wizard but keeps parent + emergency contact pre-populated so siblings
  // don't have to retype the same address / phone numbers.
  const handleEnrollAnotherChild = () => {
    const carry = {
      parent_info: enrollmentData.parent_info,
    };
    setEnrollmentData((prev) => ({
      child_first_name: '',
      child_last_name: '',
      child_date_of_birth: '',
      child_age_group: 'toddler',
      child_photo_url: '',
      child_photo_file_id: '',
      child_dietary_requirements: [],
      child_special_needs: { ...EMPTY_SPECIAL_NEEDS },
      school_info: {
        attends_school: false, school_name: '', school_address: '', school_postcode: '',
        school_class: '', teacher_name: '', school_start_time: '08:45', school_end_time: '15:15',
        school_meeting_point: '', school_contact_number: '', school_notes: '',
      },
      parent_info: carry.parent_info || prev.parent_info,
      medical_info: {
        allergies: [], dietary_restrictions: [], medications: [], medical_conditions: [],
        doctor_name: '', doctor_phone: '', insurance_provider: '', insurance_policy_number: '',
        special_needs: '', notes: '',
      },
      authorized_pickups: [],
      preferred_start_date: '',
      preferred_days: [],
      preferred_sessions: [],
      ofsted_consents: prev.ofsted_consents, // keep policy acknowledgements
      parent_signature: { signature_data: '', signature_type: 'typed', signed_at: null, full_name: '' },
      terms_accepted: false,
      handbook_acknowledged: false,
      photo_consent: false,
    }));
    setSubmittedEnrollment(null);
    setCurrentStep(1);
    setError('');
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const sessions = ['breakfast', 'morning', 'afternoon', 'after_school'];

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }} data-testid="enrollment-wizard">
        {/* Post-submit confirmation screen — short-circuits the wizard
            once the parent has successfully filed the application. */}
        {submittedEnrollment ? (
          <div data-testid="enrollment-success-screen">
            <div className="modal-header">
              <div>
                <h2 style={{ margin: 0 }}>🎉 Enrollment submitted</h2>
                <p style={{ margin: '0.5rem 0 0', color: 'hsl(var(--muted-foreground))' }}>
                  Thank you, <strong>{submittedEnrollment.parent_info?.emergency_contact?.name || 'Parent'}</strong>.
                  Your application for <strong>{submittedEnrollment.child_first_name} {submittedEnrollment.child_last_name}</strong> is now with your provider for review.
                </p>
              </div>
              <button className="modal-close" onClick={onComplete} data-testid="success-close">&times;</button>
            </div>

            <div
              className="card"
              style={{ background: 'hsl(150 60% 96%)', borderColor: 'hsl(150 60% 75%)', marginBottom: '1.25rem' }}
            >
              <h3 style={{ margin: '0 0 0.5rem 0' }}>📄 Keep a copy of your enrollment & T&amp;Cs</h3>
              <p style={{ margin: 0, fontSize: '0.92rem', color: 'hsl(var(--muted-foreground))' }}>
                Your enrollment includes the Terms &amp; Conditions you accepted today.
                You can download or print a signed PDF copy at any time from your dashboard.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '0.85rem' }}>
                <a
                  href={api.getEnrollmentPrintableUrl(submittedEnrollment._id || submittedEnrollment.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  data-testid="success-download-btn"
                >
                  📥 Download / Print signed copy
                </a>
                <button
                  type="button"
                  className="btn btn-ghost"
                  data-testid="success-email-btn"
                  onClick={() => {
                    setError('');
                    alert('We\'ll email you a copy of your enrollment and Terms & Conditions once email delivery is enabled by your provider. In the meantime, please use Download / Print to save a copy.');
                  }}
                  title="Email delivery is coming with the next platform update."
                >
                  ✉️ Email me a copy (coming soon)
                </button>
              </div>
            </div>

            <div
              className="card"
              style={{ marginBottom: '1.25rem' }}
            >
              <h3 style={{ margin: '0 0 0.5rem 0' }}>👶 Do you have another child to register?</h3>
              <p style={{ margin: 0, fontSize: '0.92rem', color: 'hsl(var(--muted-foreground))' }}>
                We'll keep your address and emergency contact ready so you only need to fill in details specific to the next child.
              </p>
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleEnrollAnotherChild}
                  data-testid="success-another-child-btn"
                >
                  + Enroll another child
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onComplete}
                  data-testid="success-done-btn"
                >
                  Done — back to my dashboard
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>
            )}
          </div>
        ) : (
        <>
        <div className="modal-header">
          <div>
            <h2>Enroll Your Child</h2>
            <p style={{ margin: '0.5rem 0 0 0', color: 'hsl(var(--muted-foreground))' }}>
              Step {currentStep} of {totalSteps}
            </p>
          </div>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>

        {/* Progress bar */}
        <div style={{ 
          height: '4px', 
          background: 'hsl(var(--muted))', 
          borderRadius: '2px',
          marginBottom: '2rem'
        }}>
          <div style={{ 
            height: '100%', 
            width: `${(currentStep / totalSteps) * 100}%`,
            background: 'hsl(var(--primary))',
            borderRadius: '2px',
            transition: 'width 0.3s ease'
          }} />
        </div>

        {error && (
          <div
            className="error-message"
            style={{ marginBottom: '1rem' }}
            data-testid="enrollment-error-banner"
          >
            {error}
          </div>
        )}

        {/* Per-step issue banner — surfaces every required field that's
            missing on the current step, with a "Go to step" jump link
            for issues that live on a different step. */}
        {fieldIssues.length > 0 && (
          <div
            style={{
              marginBottom: '1.25rem',
              padding: '0.85rem 1rem',
              background: 'hsl(35 95% 95%)',
              border: '1px solid hsl(35 90% 70%)',
              borderRadius: 8,
              fontSize: '0.9rem',
            }}
            data-testid="enrollment-issues-banner"
          >
            <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
              {fieldIssues.length} item{fieldIssues.length > 1 ? 's' : ''} need{fieldIssues.length > 1 ? '' : 's'} attention before you can submit:
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }} data-testid="enrollment-issues-list">
              {fieldIssues.map((i, idx) => (
                <li key={`${i.fieldKey}-${idx}`} style={{ marginBottom: 2 }}>
                  <strong>{i.fieldLabel}</strong> · Step {i.step} ({STEP_NAMES[i.step]}) — {i.message}
                  {i.step !== currentStep && (
                    <button
                      type="button"
                      onClick={() => { setCurrentStep(i.step); setError(''); }}
                      style={{
                        marginLeft: 8,
                        background: 'transparent',
                        border: 'none',
                        color: 'hsl(220 90% 40%)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit',
                      }}
                      data-testid={`issue-jump-${i.fieldKey}`}
                    >
                      Go to step {i.step} →
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Step 1: Child Information */}
        {currentStep === 1 && (
          <div data-testid="step-child-info">
            <h3 style={{ marginBottom: '1.5rem' }}>Child Information</h3>
            
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={enrollmentData.child_first_name}
                onChange={(e) => handleChange('child_first_name', e.target.value)}
                required
                data-testid="child-first-name"
              />
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={enrollmentData.child_last_name}
                onChange={(e) => handleChange('child_last_name', e.target.value)}
                required
                data-testid="child-last-name"
              />
            </div>

            <div className="form-group">
              <label>Date of Birth *</label>
              <input
                type="date"
                value={enrollmentData.child_date_of_birth}
                onChange={(e) => handleChange('child_date_of_birth', e.target.value)}
                required
                data-testid="child-dob"
              />
            </div>

            <div className="form-group">
              <label>Age Group *</label>
              <select
                value={enrollmentData.child_age_group}
                onChange={(e) => handleChange('child_age_group', e.target.value)}
                data-testid="child-age-group"
              >
                <option value="infant">Infant (0-2 years)</option>
                <option value="toddler">Toddler (2-3 years)</option>
                <option value="preschool">Preschool (3-5 years)</option>
                <option value="primary">Primary (5-11 years)</option>
              </select>
            </div>

            {/* Services required — drives whether school details are
                mandatory. Asked here on Step 1 so the parent knows up
                front what fields they need to fill in. */}
            <div className={`form-group ${errorClass('services_required')}`}>
              <label>Which services are you signing up for? *</label>
              <small style={{ display: 'block', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                Pick all that apply. Breakfast Club and After-School Club need school details (added below).
              </small>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }} data-testid="services-required">
                {SERVICE_TYPES.map((svc) => {
                  const checked = (enrollmentData.services_required || []).includes(svc.code);
                  return (
                    <label
                      key={svc.code}
                      data-testid={`service-${svc.code}`}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                        padding: '0.6rem 0.85rem',
                        border: `1px solid ${checked ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                        background: checked ? 'hsl(var(--primary)/0.08)' : 'hsl(var(--background))',
                        borderRadius: 6, cursor: 'pointer', minWidth: 220, flex: '1 0 220px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleServiceRequired(svc.code)}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ fontSize: '0.9rem' }}>
                        <strong>{svc.label}</strong>
                        {svc.requiresSchool && (
                          <span style={{
                            marginLeft: 6, fontSize: '0.65rem', padding: '0.05rem 0.35rem',
                            borderRadius: 999, background: 'hsl(35 95% 90%)', color: 'hsl(35 80% 30%)',
                          }}>
                            School details required
                          </span>
                        )}
                        <br />
                        <small style={{ color: 'hsl(var(--muted-foreground))' }}>{svc.hint}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
              {fieldErrorEl('services_required')}
            </div>

            <div className="form-group">
              <label>Profile Photo (optional)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.25rem' }}>
                <div
                  style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flex: '0 0 auto',
                    background: 'hsl(var(--muted))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid hsl(var(--border))',
                  }}
                >
                  {enrollmentData.child_photo_url ? (
                    <img
                      src={enrollmentData.child_photo_url}
                      alt="Child preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      data-testid="child-photo-preview"
                    />
                  ) : (
                    <span style={{ fontSize: '1.75rem', color: 'hsl(var(--muted-foreground))' }} aria-hidden="true">👶</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleChildPhotoUpload}
                    disabled={childPhotoUploading}
                    data-testid="child-photo-url"
                    style={{ display: 'block', width: '100%' }}
                  />
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    PNG, JPEG or WebP — max 5 MB.
                    {childPhotoUploading && <> Uploading…</>}
                  </small>
                  {childPhotoError && (
                    <div
                      style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'hsl(var(--destructive))' }}
                      data-testid="child-photo-error"
                    >
                      {childPhotoError}
                    </div>
                  )}
                </div>
                {enrollmentData.child_photo_url && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleChildPhotoClear}
                    data-testid="child-photo-clear"
                    style={{ flex: '0 0 auto' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* School Information — shown whenever any service is
                selected. Required only for Breakfast / After-School
                Club; otherwise optional supporting context. */}
            {schoolInfoIsShown() && (
              <div style={{ 
                marginTop: '2rem', 
                padding: '1.5rem', 
                background: 'hsl(var(--muted))',
                borderRadius: '6px',
                border: `2px solid ${schoolInfoIsRequired() ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`
              }} data-testid="school-info-section">
                <h4 style={{ marginBottom: '0.5rem', color: schoolInfoIsRequired() ? 'hsl(var(--primary))' : 'hsl(var(--foreground))' }}>
                  🏫 School Information {schoolInfoIsRequired() ? '(Required)' : '(Optional)'}
                </h4>
                <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
                  {schoolInfoIsRequired()
                    ? 'Required so our drivers know which school to collect from and where exactly to meet your child.'
                    : 'Optional — fill in if you want us to keep school details on file (e.g. for occasional school pickups during a holiday club).'}
                </p>

                <div className={`form-group ${errorClass('school_name')}`}>
                  <label>School Name {schoolInfoIsRequired() ? '*' : ''}</label>
                  <input
                    type="text"
                    value={enrollmentData.school_info.school_name}
                    onChange={(e) => handleSchoolInfoChange('school_name', e.target.value)}
                    required={schoolInfoIsRequired()}
                    placeholder="St. Mary's Primary School"
                    data-testid="school-name"
                  />
                  {fieldErrorEl('school_name')}
                </div>

                <div className={`form-group ${errorClass('school_address')}`}>
                  <label>School Address {schoolInfoIsRequired() ? '*' : ''}</label>
                  <input
                    type="text"
                    value={enrollmentData.school_info.school_address}
                    onChange={(e) => handleSchoolInfoChange('school_address', e.target.value)}
                    required={schoolInfoIsRequired()}
                    placeholder="123 High Street"
                    data-testid="school-address"
                  />
                  {fieldErrorEl('school_address')}
                </div>

                <div className={`form-group ${errorClass('school_postcode')}`}>
                  <label>School Postcode {schoolInfoIsRequired() ? '*' : ''}</label>
                  <input
                    type="text"
                    value={enrollmentData.school_info.school_postcode}
                    onChange={(e) => handleSchoolInfoChange('school_postcode', e.target.value)}
                    required={schoolInfoIsRequired()}
                    placeholder="SW1A 1AA"
                    data-testid="school-postcode"
                  />
                  {fieldErrorEl('school_postcode')}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className={`form-group ${errorClass('school_class')}`}>
                    <label>Class / Year {schoolInfoIsRequired() ? '*' : ''}</label>
                    <input
                      type="text"
                      value={enrollmentData.school_info.school_class}
                      onChange={(e) => handleSchoolInfoChange('school_class', e.target.value)}
                      required={schoolInfoIsRequired()}
                      placeholder="Year 2"
                      data-testid="school-class"
                    />
                    {fieldErrorEl('school_class')}
                  </div>

                  <div className={`form-group ${errorClass('teacher_name')}`}>
                    <label>Class Teacher {schoolInfoIsRequired() ? '*' : ''}</label>
                    <input
                      type="text"
                      value={enrollmentData.school_info.teacher_name}
                      onChange={(e) => handleSchoolInfoChange('teacher_name', e.target.value)}
                      required={schoolInfoIsRequired()}
                      placeholder="Mrs. Johnson"
                      data-testid="teacher-name"
                    />
                    {fieldErrorEl('teacher_name')}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>School Start Time</label>
                    <input
                      type="time"
                      value={enrollmentData.school_info.school_start_time}
                      onChange={(e) => handleSchoolInfoChange('school_start_time', e.target.value)}
                      data-testid="school-start-time"
                    />
                  </div>

                  <div className="form-group">
                    <label>School End Time</label>
                    <input
                      type="time"
                      value={enrollmentData.school_info.school_end_time}
                      onChange={(e) => handleSchoolInfoChange('school_end_time', e.target.value)}
                      data-testid="school-end-time"
                    />
                  </div>
                </div>

                <div className={`form-group ${errorClass('school_meeting_point')}`}>
                  <label>Collection Location {schoolInfoIsRequired() ? '*' : ''}</label>
                  <input
                    type="text"
                    value={enrollmentData.school_info.school_meeting_point}
                    onChange={(e) => handleSchoolInfoChange('school_meeting_point', e.target.value)}
                    required={schoolInfoIsRequired()}
                    placeholder="Main gate / Year 2 playground / Reception desk"
                    data-testid="school-meeting-point"
                  />
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Where exactly should our driver collect your child from school?
                  </small>
                  {fieldErrorEl('school_meeting_point')}
                </div>

                <div className="form-group">
                  <label>School Contact Number</label>
                  <input
                    type="tel"
                    value={enrollmentData.school_info.school_contact_number}
                    onChange={(e) => handleSchoolInfoChange('school_contact_number', e.target.value)}
                    placeholder="020 1234 5678"
                    data-testid="school-contact"
                  />
                </div>

                <div className="form-group">
                  <label>Special Instructions</label>
                  <textarea
                    rows="2"
                    value={enrollmentData.school_info.school_notes}
                    onChange={(e) => handleSchoolInfoChange('school_notes', e.target.value)}
                    placeholder="Any special collection instructions..."
                    data-testid="school-notes"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Parent Information */}
        {currentStep === 2 && (
          <div data-testid="step-parent-info">
            <h3 style={{ marginBottom: '1.5rem' }}>Parent/Guardian Information</h3>

            <div className="form-group">
              <label>Full name *</label>
              <input
                type="text"
                value={enrollmentData.parent_info.full_name || ''}
                onChange={(e) => handleNestedChange('parent_info', 'full_name', e.target.value)}
                required
                data-testid="parent-full-name"
                placeholder="As you'd like it to appear on the signed application"
              />
              {fieldErrorEl('parent_full_name')}
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={enrollmentData.parent_info.email || ''}
                onChange={(e) => handleNestedChange('parent_info', 'email', e.target.value)}
                required
                data-testid="parent-email"
              />
              {fieldErrorEl('parent_email')}
            </div>

            <div className="form-group">
              <label>Relationship to child *</label>
              <select
                value={enrollmentData.parent_info.relationship_to_child || 'parent'}
                onChange={(e) => handleNestedChange('parent_info', 'relationship_to_child', e.target.value)}
                required
                data-testid="parent-relationship"
              >
                <option value="parent">Parent</option>
                <option value="guardian">Legal guardian</option>
                <option value="grandparent">Grandparent</option>
                <option value="foster_parent">Foster parent</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Address Line 1 *</label>
              <input
                type="text"
                value={enrollmentData.parent_info.address_line1}
                onChange={(e) => handleNestedChange('parent_info', 'address_line1', e.target.value)}
                required
                data-testid="address-line1"
              />
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                value={enrollmentData.parent_info.address_line2}
                onChange={(e) => handleNestedChange('parent_info', 'address_line2', e.target.value)}
                data-testid="address-line2"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={enrollmentData.parent_info.city}
                  onChange={(e) => handleNestedChange('parent_info', 'city', e.target.value)}
                  required
                  data-testid="city"
                />
              </div>

              <div className="form-group">
                <label>Postcode *</label>
                <input
                  type="text"
                  value={enrollmentData.parent_info.postcode}
                  onChange={(e) => handleNestedChange('parent_info', 'postcode', e.target.value)}
                  required
                  data-testid="postcode"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                value={enrollmentData.parent_info.phone}
                onChange={(e) => handleNestedChange('parent_info', 'phone', e.target.value)}
                required
                data-testid="phone"
              />
            </div>

            <div className="form-group">
              <label>Occupation</label>
              <input
                type="text"
                value={enrollmentData.parent_info.occupation}
                onChange={(e) => handleNestedChange('parent_info', 'occupation', e.target.value)}
                data-testid="occupation"
              />
            </div>

            <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Emergency Contact *</h4>

            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={enrollmentData.parent_info.emergency_contact.name}
                onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                required
                data-testid="emergency-name"
              />
            </div>

            <div className="form-group">
              <label>Relationship *</label>
              <input
                type="text"
                value={enrollmentData.parent_info.emergency_contact.relationship}
                onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
                required
                data-testid="emergency-relationship"
              />
            </div>

            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                value={enrollmentData.parent_info.emergency_contact.phone}
                onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                required
                data-testid="emergency-phone"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={enrollmentData.parent_info.emergency_contact.email}
                onChange={(e) => handleEmergencyContactChange('email', e.target.value)}
                data-testid="emergency-email"
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={enrollmentData.parent_info.emergency_contact.can_pickup}
                  onChange={(e) => handleEmergencyContactChange('can_pickup', e.target.checked)}
                  data-testid="emergency-can-pickup"
                />
                Can pick up child
              </label>
            </div>
          </div>
        )}

        {/* Step 3: Medical & Emergency Information */}
        {currentStep === 3 && (
          <div data-testid="step-medical-info">
            <h3 style={{ marginBottom: '1.5rem' }}>Medical & Health Information</h3>
            
            <div className="form-group">
              <label>Allergies (one per line)</label>
              <textarea
                rows="3"
                value={enrollmentData.medical_info.allergies.join('\n')}
                onChange={(e) => handleArrayChange('medical_info', 'allergies', e.target.value)}
                placeholder="e.g., Peanuts\nDairy\nPenicillin"
                data-testid="allergies"
              />
            </div>

            {/* Structured SEND + dietary picker — same component as the
                Parent Quick Add Child modal so the experience matches. */}
            <SpecialNeedsAndDietPicker
              dietary={enrollmentData.child_dietary_requirements}
              onDietaryChange={(next) => handleChange('child_dietary_requirements', next)}
              specialNeeds={enrollmentData.child_special_needs}
              onSpecialNeedsChange={(next) => handleChange('child_special_needs', next)}
              testIdPrefix="wiz-"
            />

            <div className="form-group">
              <label>Current Medications (one per line)</label>
              <textarea
                rows="3"
                value={enrollmentData.medical_info.medications.join('\n')}
                onChange={(e) => handleArrayChange('medical_info', 'medications', e.target.value)}
                placeholder="e.g., EpiPen for bee stings\nInhaler as needed"
                data-testid="medications"
              />
            </div>

            <div className="form-group">
              <label>Medical Conditions (one per line)</label>
              <textarea
                rows="3"
                value={enrollmentData.medical_info.medical_conditions.join('\n')}
                onChange={(e) => handleArrayChange('medical_info', 'medical_conditions', e.target.value)}
                placeholder="e.g., Asthma\nDiabetes"
                data-testid="medical-conditions"
              />
            </div>

            <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Doctor Information</h4>

            <div className="form-group">
              <label>Doctor Name</label>
              <input
                type="text"
                value={enrollmentData.medical_info.doctor_name}
                onChange={(e) => handleNestedChange('medical_info', 'doctor_name', e.target.value)}
                data-testid="doctor-name"
              />
            </div>

            <div className="form-group">
              <label>Doctor Phone</label>
              <input
                type="tel"
                value={enrollmentData.medical_info.doctor_phone}
                onChange={(e) => handleNestedChange('medical_info', 'doctor_phone', e.target.value)}
                data-testid="doctor-phone"
              />
            </div>

            <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Insurance Information</h4>

            <div className="form-group">
              <label>Insurance Provider</label>
              <input
                type="text"
                value={enrollmentData.medical_info.insurance_provider}
                onChange={(e) => handleNestedChange('medical_info', 'insurance_provider', e.target.value)}
                data-testid="insurance-provider"
              />
            </div>

            <div className="form-group">
              <label>Policy Number</label>
              <input
                type="text"
                value={enrollmentData.medical_info.insurance_policy_number}
                onChange={(e) => handleNestedChange('medical_info', 'insurance_policy_number', e.target.value)}
                data-testid="insurance-policy"
              />
            </div>

            <div className="form-group">
              <label>Additional Notes</label>
              <textarea
                rows="3"
                value={enrollmentData.medical_info.notes}
                onChange={(e) => handleNestedChange('medical_info', 'notes', e.target.value)}
                placeholder="Any additional medical or health information..."
                data-testid="medical-notes"
              />
            </div>

            <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Authorized Pickup</h4>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              List people authorized to pick up your child
            </p>

            {enrollmentData.authorized_pickups.map((pickup, index) => (
              <div key={index} style={{ 
                padding: '1rem', 
                background: 'hsl(var(--muted))', 
                borderRadius: '6px',
                marginBottom: '0.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{pickup.name}</strong> ({pickup.relationship})<br />
                  <span style={{ fontSize: '0.875rem' }}>{pickup.phone}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => removeAuthorizedPickup(index)}
                  style={{ padding: '0.5rem' }}
                >
                  Remove
                </button>
              </div>
            ))}

            <div style={{ 
              padding: '1rem', 
              background: 'hsl(var(--muted))', 
              borderRadius: '6px',
              marginTop: '1rem'
            }}>
              <h5 style={{ marginBottom: '1rem' }}>Add Authorized Person</h5>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newPickup.name}
                  onChange={(e) => setNewPickup({ ...newPickup, name: e.target.value })}
                  data-testid="pickup-name"
                />
              </div>
              <div className="form-group">
                <label>Relationship</label>
                <input
                  type="text"
                  value={newPickup.relationship}
                  onChange={(e) => setNewPickup({ ...newPickup, relationship: e.target.value })}
                  data-testid="pickup-relationship"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={newPickup.phone}
                  onChange={(e) => setNewPickup({ ...newPickup, phone: e.target.value })}
                  data-testid="pickup-phone"
                />
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addAuthorizedPickup}
                data-testid="add-pickup-btn"
              >
                + Add Person
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Program Preferences */}
        {currentStep === 4 && (
          <div data-testid="step-program-prefs">
            <h3 style={{ marginBottom: '1.5rem' }}>Program Preferences</h3>
            
            <div className="form-group">
              <label>Preferred Start Date</label>
              <input
                type="date"
                value={enrollmentData.preferred_start_date}
                onChange={(e) => handleChange('preferred_start_date', e.target.value)}
                data-testid="start-date"
              />
            </div>

            <div className="form-group">
              <label>Preferred Days</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {days.map(day => (
                  <button
                    key={day}
                    type="button"
                    className={`btn ${enrollmentData.preferred_days.includes(day) ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => handleDayToggle(day)}
                    style={{ textTransform: 'capitalize' }}
                    data-testid={`day-${day}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Preferred Sessions</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {sessions.map(session => (
                  <button
                    key={session}
                    type="button"
                    className={`btn ${enrollmentData.preferred_sessions.includes(session) ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => handleSessionToggle(session)}
                    style={{ textTransform: 'capitalize' }}
                    data-testid={`session-${session}`}
                  >
                    {session.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/*
              Payment plan selector. Rules (see enrollment_models.py):
                • Everyone can choose Pay as you go — the default.
                • Monthly plans are DISABLED when the family selected
                  ONLY Holiday Club (Step 1). This is because holiday
                  club runs in defined weeks, not a continuous term.
                • The 5–10% discount hint is displayed but not applied
                  automatically — admin confirms the exact rate later.
            */}
            {(() => {
              const services = enrollmentData.services_required || [];
              const monthlyBlocked = services.length === 1 && services[0] === 'holiday_club';
              const options = [
                {
                  key: 'pay_as_you_go',
                  label: '💷 Pay as you go',
                  hint: 'Charged per session. Includes block bookings you buy as needed. Best if you use us occasionally.',
                  disabled: false,
                },
                {
                  key: 'monthly_term_time',
                  label: '📆 Monthly — term-time only (11 payments)',
                  hint: '39-week service, Sept → July. Spread evenly across 11 months. Typically 5–10% cheaper per day than pay-as-you-go — final rate confirmed by our team.',
                  disabled: monthlyBlocked,
                },
                {
                  key: 'monthly_full_year',
                  label: '🗓️ Monthly — full year (12 payments)',
                  hint: '52-week service, Sept → Aug. Spread evenly across 12 months. Typically 5–10% cheaper per day than pay-as-you-go — final rate confirmed by our team.',
                  disabled: monthlyBlocked,
                },
              ];
              return (
                <div className="form-group" data-testid="payment-plan-group">
                  <label>How would you like to pay?</label>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '-0.35rem', marginBottom: '0.75rem' }}>
                    You can change this later with our admin team.
                  </p>
                  {monthlyBlocked && (
                    <div
                      style={{
                        padding: '0.55rem 0.75rem',
                        background: 'hsl(38 92% 96%)',
                        border: '1px solid hsl(38 92% 80%)',
                        borderRadius: 6,
                        fontSize: '0.8rem',
                        color: 'hsl(38 92% 30%)',
                        marginBottom: '0.75rem',
                      }}
                      data-testid="payment-plan-holiday-only-notice"
                    >
                      💡 Because you only selected <strong>Holiday Club</strong>, only pay-as-you-go is available. Monthly plans apply to term-time services.
                    </div>
                  )}
                  <div style={{ display: 'grid', gap: '0.6rem' }}>
                    {options.map(opt => {
                      const isSelected = enrollmentData.payment_plan === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          disabled={opt.disabled}
                          onClick={() => !opt.disabled && handleChange('payment_plan', opt.key)}
                          data-testid={`payment-plan-${opt.key}`}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '0.75rem 0.9rem',
                            borderRadius: 8,
                            border: `2px solid ${isSelected ? 'hsl(210 80% 50%)' : 'hsl(var(--border))'}`,
                            background: opt.disabled ? '#f3f4f6' : (isSelected ? 'hsl(210 80% 96%)' : '#fff'),
                            color: opt.disabled ? '#9ca3af' : 'inherit',
                            cursor: opt.disabled ? 'not-allowed' : 'pointer',
                            fontWeight: isSelected ? 600 : 400,
                            transition: 'border-color 120ms',
                          }}
                        >
                          <div style={{ fontSize: '0.95rem' }}>{opt.label}</div>
                          <div style={{ fontSize: '0.78rem', color: opt.disabled ? '#9ca3af' : '#6b7280', marginTop: 4, fontWeight: 400 }}>
                            {opt.hint}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Step 5: Review & Terms */}
        {currentStep === 5 && (
          <div data-testid="step-review">
            <h3 style={{ marginBottom: '1.5rem' }}>Review & Acceptance</h3>
            
            <div style={{ 
              padding: '1.5rem', 
              background: 'hsl(var(--muted))', 
              borderRadius: '6px',
              marginBottom: '2rem'
            }}>
              <h4 style={{ marginBottom: '1rem' }}>Application Summary</h4>
              
              <div style={{ marginBottom: '1rem' }}>
                <strong>Child:</strong> {enrollmentData.child_first_name} {enrollmentData.child_last_name}<br />
                <strong>DOB:</strong> {enrollmentData.child_date_of_birth}<br />
                <strong>Age Group:</strong> {enrollmentData.child_age_group}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Address:</strong> {enrollmentData.parent_info.address_line1}, {enrollmentData.parent_info.city}, {enrollmentData.parent_info.postcode}<br />
                <strong>Phone:</strong> {enrollmentData.parent_info.phone}
              </div>

              {enrollmentData.medical_info.allergies.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Allergies:</strong> {enrollmentData.medical_info.allergies.join(', ')}
                </div>
              )}

              {enrollmentData.child_dietary_requirements?.length > 0 && (
                <div style={{ marginBottom: '1rem' }} data-testid="review-dietary">
                  <strong>Dietary requirements:</strong> {enrollmentData.child_dietary_requirements.join(', ')}
                </div>
              )}

              {enrollmentData.child_special_needs?.has_sen && (
                <div style={{ marginBottom: '1rem' }} data-testid="review-sen">
                  <strong>SEND:</strong>{' '}
                  {enrollmentData.child_special_needs.sen_status === 'ehcp'
                    ? 'EHC Plan (EHCP)'
                    : enrollmentData.child_special_needs.sen_status === 'sen_support'
                      ? 'SEN Support'
                      : 'Identified'}
                  {enrollmentData.child_special_needs.categories?.length > 0 && (
                    <> — {enrollmentData.child_special_needs.categories.join(', ')}</>
                  )}
                </div>
              )}

              {enrollmentData.preferred_days.length > 0 && (
                <div>
                  <strong>Preferred Days:</strong> {enrollmentData.preferred_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                </div>
              )}
            </div>

            <div className="form-group" data-testid="terms-block">
              {/* Inline scrollable T&Cs panel — falls back gracefully when
                  the org has no published document. */}
              {orgTerms && orgTerms.text && (
                <div
                  style={{
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    padding: '1rem',
                    background: 'hsl(var(--muted) / 0.4)',
                    marginBottom: '0.85rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <strong>Terms &amp; Conditions</strong>
                    <span style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                      Version {orgTerms.version}
                      {orgTerms.updated_at ? ` · updated ${new Date(orgTerms.updated_at).toLocaleDateString()}` : ''}
                    </span>
                  </div>
                  {orgTerms.url && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <a
                        href={orgTerms.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="terms-external-link"
                      >
                        Open the full document in a new tab ↗
                      </a>
                    </div>
                  )}
                  <div
                    style={{
                      maxHeight: '260px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.825rem',
                      lineHeight: 1.55,
                      padding: '0.85rem',
                      background: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                    data-testid="terms-text"
                  >
                    {orgTerms.text}
                  </div>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={enrollmentData.terms_accepted}
                  onChange={(e) => handleChange('terms_accepted', e.target.checked)}
                  style={{ marginTop: '0.25rem' }}
                  data-testid="terms-checkbox"
                />
                <span>
                  I have read and accept the {orgTerms ? <>Terms &amp; Conditions <strong>(v{orgTerms.version})</strong></> : <strong>Terms and Conditions</strong>} *
                  <br />
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    By checking this box you confirm that the information you have provided is accurate, you are the parent or legal guardian, and you accept the document above.
                  </small>
                </span>
              </label>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={enrollmentData.handbook_acknowledged}
                  onChange={(e) => handleChange('handbook_acknowledged', e.target.checked)}
                  style={{ marginTop: '0.25rem' }}
                  data-testid="handbook-checkbox"
                />
                <span>
                  I have read and understood the <strong>Parent Handbook</strong> *
                  <br />
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    The handbook covers our policies, procedures, and expectations.
                  </small>
                </span>
              </label>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={enrollmentData.photo_consent}
                  onChange={(e) => handleChange('photo_consent', e.target.checked)}
                  style={{ marginTop: '0.25rem' }}
                  data-testid="photo-consent-checkbox"
                />
                <span>
                  I consent to my child being photographed for educational purposes
                  <br />
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Photos may be used in moments feed and classroom documentation.
                  </small>
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Step 6: OFSTED Consents & Signature */}
        {currentStep === 6 && (
          <div data-testid="step-ofsted-consents">
            <h3 style={{ marginBottom: '0.5rem' }}>OFSTED Consents & Parent Signature</h3>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1.5rem' }}>
              As required by OFSTED regulations, please review and provide consent for the following:
            </p>

            {/* Live pre-submit readiness checklist — shows in real time
                what (if anything) is still missing across the whole wizard,
                so the parent never wonders "why won't Submit work?" */}
            {(() => {
              const remaining = validateAllIssues();
              if (remaining.length === 0) {
                return (
                  <div
                    style={{
                      marginBottom: '1.5rem',
                      padding: '0.85rem 1rem',
                      background: 'hsl(150 60% 96%)',
                      border: '1px solid hsl(150 60% 70%)',
                      borderRadius: 8,
                      fontSize: '0.92rem',
                    }}
                    data-testid="enrollment-ready-banner"
                  >
                    ✅ <strong>Ready to submit.</strong> All required fields are complete.
                  </div>
                );
              }
              // Group remaining items by step for the checklist.
              const byStep = remaining.reduce((acc, it) => {
                (acc[it.step] = acc[it.step] || []).push(it);
                return acc;
              }, {});
              return (
                <div
                  style={{
                    marginBottom: '1.5rem',
                    padding: '0.85rem 1rem',
                    background: 'hsl(35 95% 96%)',
                    border: '1px solid hsl(35 90% 70%)',
                    borderRadius: 8,
                    fontSize: '0.9rem',
                  }}
                  data-testid="enrollment-readiness-checklist"
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
                    Before submitting, please complete {remaining.length} item{remaining.length > 1 ? 's' : ''}:
                  </div>
                  {Object.entries(byStep).map(([step, items]) => (
                    <div key={step} style={{ marginBottom: '0.4rem' }}>
                      <button
                        type="button"
                        onClick={() => { setCurrentStep(Number(step)); setError(''); }}
                        style={{
                          background: 'transparent', border: 'none',
                          padding: 0, font: 'inherit', cursor: 'pointer',
                          color: 'hsl(220 90% 40%)', textDecoration: 'underline',
                          fontWeight: 600,
                        }}
                        data-testid={`readiness-jump-step-${step}`}
                      >
                        Step {step} · {STEP_NAMES[step]} →
                      </button>
                      <ul style={{ margin: '0.2rem 0 0 1.4rem' }}>
                        {items.map((i, idx) => (
                          <li key={`${i.fieldKey}-${idx}`} data-testid={`readiness-${i.fieldKey}`}>
                            {i.fieldLabel}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Emergency & Medical Consents */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: 'hsl(var(--primary))' }}>🚨 Emergency & Medical (Required)</h4>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.emergency_medical_treatment.granted}
                    onChange={() => handleConsentToggle('emergency_medical_treatment')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-emergency-medical"
                  />
                  <span>
                    <strong>Emergency Medical Treatment *</strong><br />
                    <small>I consent to emergency medical treatment being sought for my child if required. I understand I will be contacted as soon as possible.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.medication_administration.granted}
                    onChange={() => handleConsentToggle('medication_administration')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-medication"
                  />
                  <span>
                    <strong>Medication Administration</strong><br />
                    <small>I consent to staff administering prescribed medication to my child as per my written instructions.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.sun_cream_application.granted}
                    onChange={() => handleConsentToggle('sun_cream_application')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-sun-cream"
                  />
                  <span>
                    <strong>Sun Cream Application</strong><br />
                    <small>I consent to staff applying sun cream to my child during outdoor activities.</small>
                  </span>
                </label>
              </div>
            </div>

            {/* Activities */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: 'hsl(var(--primary))' }}>🚌 Activities & Outings</h4>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.outings_excursions.granted}
                    onChange={() => handleConsentToggle('outings_excursions')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-outings"
                  />
                  <span>
                    <strong>Outings & Excursions</strong><br />
                    <small>I consent to my child participating in outings and excursions organized by the setting (separate consent may be required for specific trips).</small>
                  </span>
                </label>
              </div>
            </div>

            {/* Photography & Media */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: 'hsl(var(--primary))' }}>📸 Photography & Media</h4>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.photography_internal_learning.granted}
                    onChange={() => handleConsentToggle('photography_internal_learning')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-photo-internal"
                  />
                  <span>
                    <strong>Internal Learning & Display</strong><br />
                    <small>I consent to photographs/videos being taken for learning journals and display within the setting.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.photography_website.granted}
                    onChange={() => handleConsentToggle('photography_website')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-photo-website"
                  />
                  <span>
                    <strong>Website Use</strong><br />
                    <small>I consent to my child's image being used on the setting's website.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.photography_social_media.granted}
                    onChange={() => handleConsentToggle('photography_social_media')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-photo-social"
                  />
                  <span>
                    <strong>Social Media</strong><br />
                    <small>I consent to my child's image being used on the setting's social media channels.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.photography_marketing.granted}
                    onChange={() => handleConsentToggle('photography_marketing')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-photo-marketing"
                  />
                  <span>
                    <strong>Marketing Materials</strong><br />
                    <small>I consent to my child's image being used in promotional/marketing materials.</small>
                  </span>
                </label>
              </div>
            </div>

            {/* Information Sharing */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: 'hsl(var(--primary))' }}>🔒 Information Sharing</h4>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.information_sharing_health.granted}
                    onChange={() => handleConsentToggle('information_sharing_health')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-info-health"
                  />
                  <span>
                    <strong>Healthcare Professionals</strong><br />
                    <small>I consent to sharing relevant information with healthcare professionals when necessary.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.information_sharing_school.granted}
                    onChange={() => handleConsentToggle('information_sharing_school')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-info-school"
                  />
                  <span>
                    <strong>Schools (Transitions)</strong><br />
                    <small>I consent to sharing my child's information with schools to support smooth transitions.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.information_sharing_authority.granted}
                    onChange={() => handleConsentToggle('information_sharing_authority')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-info-authority"
                  />
                  <span>
                    <strong>Local Authority</strong><br />
                    <small>I consent to sharing information with the local authority as required.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.information_sharing_safeguarding.granted}
                    onChange={() => handleConsentToggle('information_sharing_safeguarding')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-info-safeguarding"
                  />
                  <span>
                    <strong>Safeguarding Agencies</strong><br />
                    <small>I understand information may be shared with safeguarding agencies if concerns arise (no consent required by law, but acknowledged here).</small>
                  </span>
                </label>
              </div>
            </div>

            {/* Policy Acknowledgments */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: 'hsl(var(--primary))' }}>📋 Policy Acknowledgments (Required)</h4>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.data_protection_gdpr.granted}
                    onChange={() => handleConsentToggle('data_protection_gdpr')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-gdpr"
                  />
                  <span>
                    <strong>Data Protection & GDPR *</strong><br />
                    <small>I acknowledge how my data and my child's data will be stored, processed, and protected in accordance with GDPR.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.parent_handbook.granted}
                    onChange={() => handleConsentToggle('parent_handbook')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-handbook"
                  />
                  <span>
                    <strong>Parent Handbook *</strong><br />
                    <small>I have read and understood the parent handbook, including policies on behavior, complaints, fees, and notice periods.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.behavior_policy.granted}
                    onChange={() => handleConsentToggle('behavior_policy')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-behavior"
                  />
                  <span>
                    <strong>Behavior Management Policy</strong><br />
                    <small>I understand and agree with the setting's approach to managing children's behavior.</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrollmentData.ofsted_consents.complaints_procedure.granted}
                    onChange={() => handleConsentToggle('complaints_procedure')}
                    style={{ marginTop: '0.25rem' }}
                    data-testid="consent-complaints"
                  />
                  <span>
                    <strong>Complaints Procedure</strong><br />
                    <small>I understand the process for raising concerns or complaints.</small>
                  </span>
                </label>
              </div>
            </div>

            {/* Parent Signature */}
            <div style={{ 
              padding: '1.5rem', 
              background: 'hsl(var(--muted))', 
              borderRadius: '6px',
              border: '2px solid hsl(var(--primary))'
            }}>
              <h4 style={{ marginBottom: '1rem' }}>✍️ Parent Signature (Required)</h4>
              
              <div className="form-group">
                <label>Signature Type</label>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={signatureType === 'typed'}
                      onChange={() => setSignatureType('typed')}
                      data-testid="signature-type-typed"
                    />
                    Typed Signature
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={signatureType === 'drawn'}
                      onChange={() => setSignatureType('drawn')}
                      data-testid="signature-type-drawn"
                    />
                    Draw Signature
                  </label>
                </div>
              </div>

              {signatureType === 'typed' ? (
                <div className="form-group">
                  <label>Type Your Full Name *</label>
                  <input
                    type="text"
                    value={enrollmentData.parent_signature.full_name}
                    onChange={(e) => handleSignatureChange(e.target.value)}
                    placeholder="John Smith"
                    required
                    data-testid="signature-typed-input"
                    style={{ fontFamily: 'cursive', fontSize: '1.5rem' }}
                  />
                  {enrollmentData.parent_signature.full_name && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      background: 'white',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontFamily: 'cursive', fontSize: '2rem', color: 'hsl(var(--foreground))' }}>
                        {enrollmentData.parent_signature.full_name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                        Signed: {new Date().toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-group">
                  <label>Draw Your Signature *</label>
                  <div style={{ 
                    background: 'white',
                    border: '2px solid hsl(var(--border))',
                    borderRadius: '4px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                      Canvas signature drawing will be implemented with a signature pad library
                    </p>
                    <input
                      type="text"
                      placeholder="For now, please use 'Typed Signature' option"
                      disabled
                      style={{ marginTop: '1rem' }}
                    />
                  </div>
                </div>
              )}

              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginTop: '1rem' }}>
                By signing above, I confirm that all information provided is accurate and I have read and understood all consents marked with an asterisk (*) are required.
              </p>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid hsl(var(--border))', flexWrap: 'wrap' }}>
          {currentStep > 1 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handlePrevious}
              data-testid="prev-btn"
            >
              ← Previous
            </button>
          )}

          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleSaveAndExit}
            data-testid="save-and-exit-btn"
            title="Saves your progress on this device. You can resume later from the same browser."
          >
            💾 Save &amp; continue later
          </button>

          <div style={{ flex: 1 }} />

          {currentStep < totalSteps ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
              data-testid="next-btn"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="submit-enrollment-btn"
            >
              {loading ? 'Submitting...' : 'Submit Enrollment'}
            </button>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default EnrollmentWizard;
